import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Stack,
  Pagination,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { formatDate, calculateTotalPages } from "../../../utils/Helper";
import {
  CouponCreateAction,
  CouponListAction,
  CouponUpdateAction,
} from "../../../Redux/Actions/PaymentAction";
import { useNavigate } from "react-router-dom";
import CreateCoupon, { CreateCouponPayload } from "./CreateCoupon/CreateCoupon";
import CouponUpdate, { UpdateCouponPayload } from "./CouponUpdate/CouponUpdate";

const discountTypeOptions = [
  { value: "", label: "All" },
  { value: "PERCENTAGE", label: "Percentage" },
  { value: "FIXED_AMOUNT", label: "Fixed" },
];

const discountOnOptions = [
  { value: "", label: "All" },
  { value: "PRICE", label: "Price" },
  { value: "CREDITS", label: "Credits" },
  { value: "BOTH", label: "Both" },
];

const statusOptions = [
  { value: "", label: "All" },
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "EXPIRED", label: "Expired" },
];

const sortByOptions = [
  { value: "expiry", label: "Expiry" },
  { value: "minimum_amount", label: "Min Amount" },
  { value: "maximum_amount", label: "Max Amount" },
  { value: "created_at", label: "Created At" },
];

const initialFilters = {
  code: "",
  discount_type: "",
  discount_on: "",
  status: "",
  min_amount: "",
  max_amount: "",
  expiry_after: "",
  expiry_before: "",
  sort_by: "expiry",
  sort_order: "asc",
};

const Coupon: React.FC = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const couponData = useSelector((state: any) => state.CouponListData);

  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [filters, setFilters] = useState(initialFilters);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [openUpdate, setOpenUpdate] = useState(false);
  const [selectedCouponId, setSelectedCouponId] = useState<number | null>(null);
  const [selectedUpdateData, setSelectedUpdateData] =
    useState<UpdateCouponPayload>({});

  const buildQuery = () => {
    const params: Record<string, any> = {
      page,
      page_size: rowsPerPage,
      ...filters,
    };
    Object.keys(params).forEach((k) => {
      if (params[k] === "" || params[k] == null) delete params[k];
    });
    return `?${new URLSearchParams(params).toString()}`;
  };

  const fetchCoupons = () => {
    setLoading(true);
    dispatch(CouponListAction(buildQuery()));
  };

  useEffect(() => {
    fetchCoupons();
  }, [dispatch, page, filters]);

  useEffect(() => {
    if (couponData?.data?.row) {
      setLoading(false);
      setCoupons(couponData.data.row);
    }
  }, [couponData]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const applyFilters = () => setPage(1);
  const clearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
  };

  const handleCreateCoupon = (data: CreateCouponPayload) => {
    dispatch(CouponCreateAction(data)).then(() => {
      fetchCoupons();
    });
  };

  const handleCouponUpdate = (payload: UpdateCouponPayload, params: string) => {
    dispatch(CouponUpdateAction(payload, params)).then(() => {
      fetchCoupons();
      setOpenUpdate(false);
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ p: 2 }}>
          <Breadcrumb
            pageTitle="Coupons"
            breadcrumbs={[
              { label: "Coupons", path: "/coupons/list", isActive: true },
            ]}
          />
        </Box>

        {/* Filters */}
        <Box sx={{ m: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField
            name="code"
            label="Code"
            value={filters.code}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            select
            name="discount_type"
            label="Discount Type"
            value={filters.discount_type}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {discountTypeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            name="discount_on"
            label="Discount On"
            value={filters.discount_on}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {discountOnOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            name="status"
            label="Status"
            value={filters.status}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {statusOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            name="min_amount"
            label="Min Amount"
            type="number"
            value={filters.min_amount}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="max_amount"
            label="Max Amount"
            type="number"
            value={filters.max_amount}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="expiry_after"
            label="Expiry After"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.expiry_after}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="expiry_before"
            label="Expiry Before"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.expiry_before}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            select
            name="sort_by"
            label="Sort By"
            value={filters.sort_by}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 140 }}
          >
            {sortByOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            name="sort_order"
            label="Order"
            value={filters.sort_order}
            onChange={handleFilterChange}
            size="small"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </TextField>

          <Button variant="contained" onClick={applyFilters}>
            Apply
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenForm(true)}
          >
            Create Coupon
          </Button>
        </Box>

        {/* Table */}
        <Box sx={{ m: 2 }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }} align="center">
                    S.No
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Code
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Discount On
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Discount Value
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Type
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Credit Bonus
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Min Amount
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Max Amount
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Max Usage
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Max Usage / User
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Usage Count
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Expiry
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ color: "white" }} align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coupons.map((c, idx) => (
                  <TableRow
                    key={c?.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/coupon/usage/${c.id}`)}
                  >
                    <TableCell align="center">
                      {(page - 1) * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell align="center">{c?.code}</TableCell>
                    <TableCell align="center">{c?.discount_on}</TableCell>
                    <TableCell align="center">
                      {["BOTH", "PRICE"].includes(c?.discount_on) &&
                      c?.discount_type === "FIXED_AMOUNT"
                        ? "₹"
                        : ""}
                      {c?.discount_value ?? "N/A"}
                      {c?.discount_type === "PERCENTAGE" ? "%" : ""}
                    </TableCell>
                    <TableCell align="center">{c?.discount_type}</TableCell>
                    <TableCell align="center">
                      {c?.credit_bonus ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      ₹{c?.minimum_amount ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      ₹{c?.maximum_amount ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {c?.max_usage ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {c?.max_usage_per_user ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {c?.usage_count ?? "N/A"}
                    </TableCell>
                    <TableCell align="center">
                      {formatDate(c?.expiry)}
                    </TableCell>
                    <TableCell align="center">{c?.status}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedUpdateData(c);
                          setSelectedCouponId(c?.id);
                          setOpenUpdate(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {coupons.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} align="center">
                      No coupons found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Pagination */}
        <Box sx={{ m: 2, display: "flex", justifyContent: "center" }}>
          <Stack spacing={2}>
            <Pagination
              count={calculateTotalPages(
                couponData?.data?.total_count || 0,
                rowsPerPage
              )}
              page={page}
              onChange={(_, p) => setPage(p)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        </Box>
      </div>
      <CreateCoupon
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleCreateCoupon}
      />
      {selectedCouponId !== null && (
        <CouponUpdate
          open={openUpdate}
          onClose={() => setOpenUpdate(false)}
          onSubmit={handleCouponUpdate}
          couponId={selectedCouponId}
          initialData={selectedUpdateData}
        />
      )}
      <ToastDefault />
    </>
  );
};

export default Coupon;
